import { t } from "@lingui/macro";

import type { TemplateId } from "../../convex/templateTypes";
import {
  STYLE_COLOR_KEYS,
  TEMPLATE_DEFAULTS,
  type StyleOverrides,
} from "../../convex/templateUtils";

export const TEMPLATE_PREVIEW_PALETTE_IDS = [
  "original",
  "dusty-rose",
  "sage",
  "formal",
] as const;

export type TemplatePreviewPaletteId =
  (typeof TEMPLATE_PREVIEW_PALETTE_IDS)[number];

type PaletteColors = Pick<
  Required<StyleOverrides>,
  (typeof STYLE_COLOR_KEYS)[number]
>;

export interface TemplatePreviewPalette {
  colors: PaletteColors;
  id: TemplatePreviewPaletteId;
  label: string;
}

export const DEFAULT_TEMPLATE_PREVIEW_PALETTE_ID: TemplatePreviewPaletteId =
  "original";

function toPaletteColors(
  source: Pick<Required<StyleOverrides>, (typeof STYLE_COLOR_KEYS)[number]>,
): PaletteColors {
  return STYLE_COLOR_KEYS.reduce((acc, key) => {
    acc[key] = source[key];
    return acc;
  }, {} as PaletteColors);
}

const SHARED_TEMPLATE_PREVIEW_PALETTES: Record<
  Exclude<TemplatePreviewPaletteId, "original">,
  TemplatePreviewPalette
> = {
  "dusty-rose": {
    colors: {
      colorBackground: "#fdf6f6",
      colorBackgroundAlt: "#f5ecec",
      colorMuted: "#9a8a8a",
      colorPrimary: "#b76e79",
      colorSecondary: "#3d3d3d",
    },
    id: "dusty-rose",
    label: t`Rosa Empolvado`,
  },
  formal: {
    colors: {
      colorBackground: "#fdfbf5",
      colorBackgroundAlt: "#f5f0e6",
      colorMuted: "#8a7e6e",
      colorPrimary: "#b8860b",
      colorSecondary: "#1c2340",
    },
    id: "formal",
    label: t`Azul Formal`,
  },
  sage: {
    colors: {
      colorBackground: "#f5f8f2",
      colorBackgroundAlt: "#eaf0e5",
      colorMuted: "#7a8a72",
      colorPrimary: "#87a96b",
      colorSecondary: "#2f3e2f",
    },
    id: "sage",
    label: t`Verde Salvia`,
  },
};

export function isTemplatePreviewPaletteId(
  value: unknown,
): value is TemplatePreviewPaletteId {
  return (
    typeof value === "string" &&
    (TEMPLATE_PREVIEW_PALETTE_IDS as readonly string[]).includes(value)
  );
}

export function getTemplatePreviewPalettes(
  templateId: TemplateId,
): Array<TemplatePreviewPalette> {
  return [
    {
      colors: toPaletteColors(TEMPLATE_DEFAULTS[templateId]),
      id: DEFAULT_TEMPLATE_PREVIEW_PALETTE_ID,
      label: t`Original`,
    },
    SHARED_TEMPLATE_PREVIEW_PALETTES["dusty-rose"],
    SHARED_TEMPLATE_PREVIEW_PALETTES.sage,
    SHARED_TEMPLATE_PREVIEW_PALETTES.formal,
  ];
}

export function getTemplatePreviewStyleOverrides(
  templateId: TemplateId,
  paletteId: TemplatePreviewPaletteId,
): StyleOverrides | undefined {
  if (paletteId === DEFAULT_TEMPLATE_PREVIEW_PALETTE_ID) {
    return undefined;
  }

  return getTemplatePreviewPalettes(templateId).find(
    (palette) => palette.id === paletteId,
  )?.colors;
}
